// API utility functions
import { simulateAPI } from './apiTest';

// For development: use explicit backend URL, for production: use current origin for reverse proxy
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  (process.env.NODE_ENV === 'development' ? 'http://localhost:5000' : window.location.origin);
// Temporary CORS proxy for testing - remove this in production
const CORS_PROXY = 'https://cors-anywhere.herokuapp.com/';
const IS_SIMULATION = process.env.REACT_APP_IS_SIMULATION === 'true';
const DATABASE_NAME = process.env.REACT_APP_DATABASE;

// Minimal, non-noisy runtime hint in dev only
if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line no-console
  console.log('🔧 API Configuration:', {
    baseUrl: API_BASE_URL,
    isSimulation: IS_SIMULATION,
    mode: IS_SIMULATION ? 'SIMULATION' : 'REAL API'
  });
}

// Helper function to make API calls with authentication
const apiCall = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  // If simulation is enabled, use simulation directly
  if (IS_SIMULATION) {
    return await simulateAPI(endpoint, options.method || 'GET', 
      options.body ? JSON.parse(options.body) : null);
  }
  
  // Get token from localStorage for authenticated requests
  const token = localStorage.getItem('accessToken');

  const isFormData = typeof FormData !== 'undefined' && options && options.body instanceof FormData;
  
  // Debug logging for token issues
  if (endpoint.includes('/pdf/upload')) {
    console.log('🔍 PDF Upload Debug:', {
      endpoint,
      hasToken: !!token,
      tokenLength: token ? token.length : 0,
      tokenStart: token ? token.substring(0, 20) + '...' : 'none',
      localStorageKeys: Object.keys(localStorage),
      options
    });
  }
  
  // Build headers first, then place them back onto the final config so
  // options.headers cannot overwrite the merged headers object.
  const mergedHeaders = {
    // Only set JSON content-type when NOT sending FormData
    ...(!isFormData && { 'Content-Type': 'application/json' }),
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...(DATABASE_NAME && { 'x-database': DATABASE_NAME }),
    ...(options.headers || {}),
  };

  const config = {
    ...options,
    headers: mergedHeaders,
  };

  // Debug logging for final config
  if (endpoint.includes('/pdf/upload')) {
    console.log('🔍 Final Config:', {
      url,
      method: config.method,
      headers: config.headers,
      hasAuthHeader: !!config.headers.Authorization
    });
  }

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      // Try to parse error response body for more details
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        // If parsing fails, use generic error
        errorData = { message: `HTTP error! status: ${response.status}` };
      }
      
      // Create error with parsed data
      const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.data = errorData;
      throw error;
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`❌ Real API call failed for ${endpoint}:`, error);
    throw error;
  }
};

// Enhanced API call with automatic token refresh
const apiCallWithRefresh = async (endpoint, options = {}) => {
  try {
    return await apiCall(endpoint, options);
  } catch (error) {
    // If the error is 401 (Unauthorized), handle authentication failure
    if (error.status === 401) {
      console.log('🔐 401 Unauthorized received for:', endpoint);
      
      // Clear all tokens and notify app immediately
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('tokenFamily');
      localStorage.removeItem('userData');
      
      // Dispatch event to force logout and redirect to login screen
      console.log('🚪 Dispatching auth:tokenExpired event - forcing logout');
      window.dispatchEvent(new CustomEvent('auth:tokenExpired'));
      
      // Still throw the error for any specific error handling
      throw error;
    }
    
    // Re-throw the original error for non-401 errors
    throw error;
  }
};

// Authentication APIs
export const authAPI = {
  register: async (userData) => {
    const endpoint = process.env.REACT_APP_REGISTER_API || '/api/auth/register';
    return apiCall(endpoint, {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  login: async (credentials) => {
    const endpoint = process.env.REACT_APP_LOGIN_API || '/api/auth/login';
    return apiCall(endpoint, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  loginWithGoogle: async (idToken) => {
    console.log('🔐 Attempting Google login with ID token...');
    
    // Use the correct endpoint: /api/auth/google
    const url = `${API_BASE_URL}/api/auth/google`;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ idToken }),
      });
      
      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('❌ Backend error:', errorData);
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      console.log('✅ Login successful:', result);
      return result;
    } catch (error) {
      console.error('❌ Google login failed:', error);
      throw error;
    }
  },

  googleAuth: async (googleToken) => {
    return apiCall(process.env.REACT_APP_GOOGLE_AUTH_API || '/api/auth/google', {
      method: 'POST',
      body: JSON.stringify({ token: googleToken }),
    });
  },

  googleLogin: async (googleToken) => {
    return apiCall(process.env.REACT_APP_GOOGLE_LOGIN_API || '/api/auth/google/login', {
      method: 'POST',
      body: JSON.stringify({ token: googleToken }),
    });
  },

  googleSignup: async (googleToken, additionalData = {}) => {
    return apiCall(process.env.REACT_APP_GOOGLE_SIGNUP_API || '/api/auth/google/signup', {
      method: 'POST',
      body: JSON.stringify({ token: googleToken, ...additionalData }),
    });
  },

  changePassword: async (passwordData) => {
    return apiCall('/api/auth/change-password', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
  },

  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    return apiCall('/api/auth/refresh-token', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    });
  },

  logout: async () => {
    const accessToken = localStorage.getItem('accessToken');
    const refreshToken = localStorage.getItem('refreshToken');
    
    if (accessToken) {
      try {
        await apiCall('/api/auth/logout', {
          method: 'POST',
          body: JSON.stringify({ refreshToken }),
        });
      } catch (error) {
        console.warn('Logout API call failed, but continuing with local cleanup:', error);
      }
    }
    
    // Always clean up local storage regardless of API call success
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenFamily');
    localStorage.removeItem('userData');
  },
};

// Security/Policy APIs
export const securityAPI = {
  getPasswordPolicy: async () => {
    const res = await apiCallWithRefresh('/api/password-policy', { method: 'GET' });
    return res && res.success ? (res.policy || null) : null;
  },
};

// Document APIs
export const documentAPI = {
  upload: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    const url = `${API_BASE_URL}/api/pdf/upload`;
    
    // If simulation is enabled, return mock data
    if (IS_SIMULATION) {
      return {
        id: "686a3cf0ba1298c8631a0698",
        originalName: file.name,
        fileName: `file-${Date.now()}-${Math.floor(Math.random() * 1000000000)}.pdf`,
        path: `uploads\\file-${Date.now()}-${Math.floor(Math.random() * 1000000000)}.pdf`,
        uploadedBy: "6868477376fd83126ca87af5",
        companyId: "6868477376fd83126ca87af3",
        uploadDate: new Date().toISOString()
      };
    }
    
    // Use enhanced API call with automatic token refresh
    return apiCallWithRefresh('/api/pdf/upload', {
      method: 'POST',
      headers: {
        // Don't set Content-Type header for FormData, let browser set it
        ...(DATABASE_NAME && { 'x-database': DATABASE_NAME }),
      },
      body: formData,
    });
  },

  getDocuments: async () => {
    // If simulation is enabled, return mock data
    if (IS_SIMULATION) {
      return [
        {
          id: "686850a45a8cfa41b528f91b",
          originalName: "AS3000.pdf",
          fileName: "file-1751666852014-548354970.pdf",
          path: "uploads\\file-1751666852014-548354970.pdf",
          uploadedBy: "6868477376fd83126ca87af5",
          companyId: "6868477376fd83126ca87af3",
          uploadDate: "2025-07-04T22:07:32.289Z"
        },
        {
          id: "6868f823d71974e150f587c2",
          originalName: "Technical_Manual.pdf",
          fileName: "file-1751709731150-864843448.pdf",
          path: "uploads\\file-1751709731150-864843448.pdf",
          uploadedBy: "6868477376fd83126ca87af5",
          companyId: "6868477376fd83126ca87af3",
          uploadDate: "2025-07-05T10:02:11.387Z"
        },
        {
          id: "6869123e6b8a714ab5e5facc",
          originalName: "Compliance_Report.pdf",
          fileName: "file-1751716413481-933714962.pdf",
          path: "uploads\\file-1751716413481-933714962.pdf",
          uploadedBy: "6868477376fd83126ca87af5",
          companyId: "6868477376fd83126ca87af3",
          uploadDate: "2025-07-05T11:53:34.482Z"
        }
      ];
    }
    
    return apiCallWithRefresh('/api/pdf/documents', { method: 'GET' });
  },

  deleteDocument: async (documentId) => {
    return apiCallWithRefresh(`/api/pdf/documents/${documentId}`, { method: 'DELETE' });
  },

  process: async (documentId, options = {}) => {
    const { clientTimeoutMs = 2000, ...payload } = options || {};
    const controller = new AbortController();
    const apiPromise = apiCallWithRefresh(`/api/pdf/process/${documentId}`, {
      method: 'POST',
      body: JSON.stringify({ ...payload }),
      signal: controller.signal,
    });
    // Proactively close the client connection after a short window so the UI never waits on it
    const timeoutId = setTimeout(() => {
      try { controller.abort(); } catch (_) {}
    }, Math.max(500, clientTimeoutMs));
    try {
      const res = await apiPromise;
      clearTimeout(timeoutId);
      return res;
    } catch (err) {
      clearTimeout(timeoutId);
      // Treat client-side abort as acceptable "fire-and-forget"
      if (err?.name === 'AbortError' || /aborted/i.test(err?.message || '')) {
        return { accepted: true, abortedByClient: true };
      }
      throw err;
    }
  },

  cancelProcess: async (documentId, payload = {}) => {
    return apiCallWithRefresh(`/api/pdf/process/${documentId}/cancel`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  deleteMetadata: async (metadataId) => {
    return apiCallWithRefresh(`/api/pdf/metadata/${metadataId}`, { method: 'DELETE' });
  },

  getMetadata: async (documentId) => {
    return apiCallWithRefresh(`/api/pdf/clauses/${documentId}`, { method: 'GET' });
  },

  // Fetch only the metadata record by documentId (no clauses body)
  getMetadataRecord: async (documentId) => {
    return apiCallWithRefresh(`/api/pdf/clauses/metadata/${documentId}`, { method: 'GET' });
  },

  // Fetch raw clauses JSON for a document (separate from metadata wrapper)
  getClausesData: async (documentId) => {
    return apiCallWithRefresh(`/api/pdf/clauses-data/${documentId}`, { method: 'GET' });
  },

  processEmbeddings: async (metadataIds, forceReprocess = false) => {
    return apiCallWithRefresh(`/api/rag/stateful/embeddings/process`, {
      method: 'POST',
      body: JSON.stringify({ 
        metadataIds: Array.isArray(metadataIds) ? metadataIds : [metadataIds],
        forceReprocess 
      }),
    });
  },

  // RAG Query API for asking questions about documents
  ragQuery: async (question, metadataIds, documentIds = [], options = {}) => {
    const {
      limit = 5,
      numResults = 3,
      timeout = 30000
    } = options;

    const requestBody = {
      question,
      metadataIds: Array.isArray(metadataIds) ? metadataIds : [metadataIds],
      documentIds: Array.isArray(documentIds) ? documentIds : [documentIds],
      limit,
      numResults,
      timeout
    };

    console.log('🔍 Making RAG API call to /api/rag/stateful/query with:', requestBody);

    return apiCallWithRefresh(`/api/rag/stateful/query`, {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  },
};

// Generic API for other endpoints
export const api = {
  get: (endpoint) => apiCallWithRefresh(endpoint, { method: 'GET' }),
  post: (endpoint, data) => apiCallWithRefresh(endpoint, { 
    method: 'POST', 
    body: JSON.stringify(data) 
  }),
  put: (endpoint, data) => apiCallWithRefresh(endpoint, { 
    method: 'PUT', 
    body: JSON.stringify(data) 
  }),
  delete: (endpoint) => apiCallWithRefresh(endpoint, { method: 'DELETE' }),

  getUserSubscriptions: () => apiCallWithRefresh('/api/user-subscriptions', { method: 'GET' }),
};

// Chat APIs
export const chatAPI = {
  ask: async ({ question, metadataIds, title, modelId, sessionId }) => {
    // Ensure metadataIds is always an array
    const metadataIdsArray = Array.isArray(metadataIds) ? metadataIds : (metadataIds ? [metadataIds] : []);
    
    const payload = {
      question,
      metadataIds: metadataIdsArray, // Send as array to backend
      title,
      modelId,
    };
    if (sessionId) {
      payload.sessionId = sessionId;
    }
    return apiCallWithRefresh('/api/chat/ask', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  /**
   * Stream chat responses using Server-Sent Events (SSE)
   * @param {Object} params - Chat parameters
   * @param {Function} onToken - Callback for each token received (token, fullContent)
   * @param {Function} onComplete - Callback when streaming completes
   * @param {Function} onError - Callback for errors
   * @returns {Object} Object with cleanup function and streamSessionId
   */
  askStream: async ({ question, metadataIds, title, modelId, sessionId }, onToken, onComplete, onError, onSession) => {
    const metadataIdsArray = Array.isArray(metadataIds) ? metadataIds : (metadataIds ? [metadataIds] : []);
    
    const payload = {
      question,
      metadataIds: metadataIdsArray,
      title,
      modelId,
    };
    if (sessionId) {
      payload.sessionId = sessionId;
    }

    const token = localStorage.getItem('accessToken');
    const url = `${API_BASE_URL}/api/chat/ask/stream`;

    const abortController = new AbortController();
    let currentStreamSessionId = null;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...(DATABASE_NAME && { 'x-database': DATABASE_NAME }),
        },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: `HTTP error! status: ${response.status}` }));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamSessionId = null;
      let fullContent = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            
            try {
              const parsed = JSON.parse(data);
              
              if (parsed.type === 'session') {
                streamSessionId = parsed.data.streamSessionId || parsed.data.sessionId;
                currentStreamSessionId = streamSessionId;
                // Notify parent about session info
                if (onSession) {
                  onSession({
                    sessionId: parsed.data.sessionId,
                    streamSessionId: parsed.data.streamSessionId
                  });
                }
              } else if (parsed.type === 'token') {
                const content = parsed.data.content;
                fullContent += content;
                if (onToken) {
                  onToken(content, fullContent);
                }
              } else if (parsed.type === 'error') {
                // Handle error from server
                const errorMessage = parsed.data?.error || parsed.data?.message || 'An error occurred during streaming';
                const streamError = new Error(errorMessage);
                streamError.isStreamError = true;
                if (onError) {
                  onError(streamError);
                }
                // Stop processing and throw error
                throw streamError;
              } else if (parsed.type === 'complete') {
                if (onComplete) {
                  onComplete({
                    sessionId: parsed.data.sessionId,
                    fullContent,
                    source: parsed.data.source,
                    relevantClauses: parsed.data.relevantClauses || [],
                    qaPairId: parsed.data.qaPairId,
                  });
                }
              }
            } catch (parseError) {
              // Re-throw stream errors
              if (parseError.isStreamError) {
                throw parseError;
              }
              console.warn('Failed to parse SSE data:', data, parseError);
            }
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Stream aborted by user');
        // Cancel on server side if we have streamSessionId
        if (currentStreamSessionId) {
          chatAPI.cancelStream(currentStreamSessionId).catch(err => 
            console.warn('Failed to cancel stream on server:', err)
          );
        }
      } else {
        console.error('Stream error:', error);
        if (onError) {
          onError(error);
        }
      }
      throw error;
    }

    // Return object with cleanup function and streamSessionId
    return {
      abort: () => {
        abortController.abort();
        // Also cancel on server side
        if (currentStreamSessionId) {
          chatAPI.cancelStream(currentStreamSessionId).catch(err => 
            console.warn('Failed to cancel stream on server:', err)
          );
        }
      },
      streamSessionId: currentStreamSessionId
    };
  },

  /**
   * Cancel an ongoing stream on the server
   * @param {string} streamSessionId - The stream session ID to cancel
   */
  cancelStream: async (streamSessionId) => {
    if (!streamSessionId) {
      console.warn('No streamSessionId provided for cancellation');
      return;
    }

    const token = localStorage.getItem('accessToken');
    const url = `${API_BASE_URL}/api/chat/ask/stream/cancel`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          ...(DATABASE_NAME && { 'x-database': DATABASE_NAME }),
        },
        body: JSON.stringify({ streamSessionId }),
      });

      if (!response.ok) {
        console.warn('Failed to cancel stream on server:', response.status);
      } else {
        console.log('Stream cancelled successfully on server');
      }
    } catch (error) {
      console.error('Error cancelling stream:', error);
      throw error;
    }
  },
};

// AI Model APIs
export const aiModelAPI = {
  getModels: async () => {
    const res = await apiCallWithRefresh('/api/aimodels', { method: 'GET' });
    return res.aiModels || [];
  },
};

// Chat Sessions API
export const chatSessionsAPI = {
  getSessions: async () => {
    const res = await apiCallWithRefresh('/api/chat/sessions', { method: 'GET' });
    return res.sessions || [];
  },
  getSessionById: async (sessionId) => {
    const res = await apiCallWithRefresh(`/api/chat/sessions/${sessionId}`, { method: 'GET' });
    return res.session || null;
  },
  deleteSession: async (sessionId) => {
    return apiCallWithRefresh(`/api/chat/sessions/${sessionId}`, { method: 'DELETE' });
  },
  submitCorrectAnswer: async (sessionId, question, answer, confidence) => {
    return apiCallWithRefresh(`/api/chat/sessions/${sessionId}/correct-answer`, {
      method: 'POST',
      body: JSON.stringify({ question, answer, confidence }),
    });
  },
};

// Utility function to fetch relevant clauses for chat messages
export const fetchRelevantClausesForMessage = async (question, answer, metadataIds = []) => {
  // Disabled external retrieval; rely on clauses returned with the answer.
  // Avoid calling /api/rag/stateful/query.
  return [];
};

// Subscription APIs
export const subscriptionAPI = {
  getPackages: async () => {
    return apiCallWithRefresh('/api/subscription-packages', { method: 'GET' });
  },
};

// Payment Providers API
export const paymentAPI = {
  getProviders: async () => {
    return apiCallWithRefresh('/api/payment-providers', { method: 'GET' });
  },
  purchaseSubscription: async (payload) => {
    return apiCallWithRefresh('/api/subscription-purchase', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  getPaymentOptions: async () => {
    return apiCallWithRefresh('/api/subscription-payment-options', { method: 'GET' });
  },
  confirmSubscriptionPurchase: async ({ paymentIntentId, purchaseId }) => {
    return apiCallWithRefresh('/api/subscription-purchase/confirm', {
      method: 'POST',
      body: JSON.stringify({ paymentIntentId, purchaseId }),
    });
  },
  redeemSubscriptionCode: async (code) => {
    return apiCallWithRefresh('/api/subscription-codes/apply', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  },
};

// Companies APIs
export const companiesAPI = {
  existsByName: async (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return { exists: false };
    const res = await apiCallWithRefresh(`/api/companies/exists?name=${encodeURIComponent(trimmed)}`, { method: 'GET' });
    if (typeof res === 'boolean') return { exists: res };
    if (res && typeof res.exists === 'boolean') return { exists: res.exists };
    return { exists: !!res?.exists };
  },
};

export { API_BASE_URL };

const apiExports = { authAPI, documentAPI, api, chatAPI, aiModelAPI, chatSessionsAPI, securityAPI, companiesAPI };
export default apiExports;
